import {
  assignTableAPIs,
  makeStateUpdater,
  type OnChangeFn,
  type RowData,
  type Table,
  type TableFeature,
  type TableFeatures,
} from "@tanstack/react-table";

interface SelectedAllTableState {
  selectedAll: boolean;
}

export interface SelectedAllOptions {
  onSelectedAllChange?: OnChangeFn<boolean>;
}

export interface SelectAllInstance {
  setSelectedAll: (value: boolean) => void;
  toggleSelectAll: () => void;
  getSelectedAll: () => boolean;
  getSelectedCount: () => number;
  getSelectedIds: () => string[];
}

declare module "@tanstack/table-core" {
  interface Plugins {
    selectedAllFeature: TableFeature;
  }
  interface TableState_FeatureMap {
    selectedAllFeature: SelectedAllTableState;
  }
  interface TableOptions_FeatureMap<
    TFeatures extends TableFeatures,
    TData extends RowData,
  > {
    selectedAllFeature: SelectedAllOptions;
  }
  interface Table_FeatureMap<
    TFeatures extends TableFeatures,
    TData extends RowData,
  > {
    selectedAllFeature: SelectAllInstance;
  }
}

type SelectedAllTable = Table<
  {
    rowSelectionFeature: TableFeature;
    rowPaginationFeature: TableFeature;
    selectedAllFeature: TableFeature;
  },
  RowData
>;

export const selectedAllFeature: TableFeature = {
  getInitialState: (state) => ({
    selectedAll: false,
    ...state,
  }),

  getDefaultTableOptions: (table) => ({
    onSelectedAllChange: makeStateUpdater("selectedAll", table),
  }),

  constructTableAPIs: (table) => {
    const t = table as unknown as SelectedAllTable;

    assignTableAPIs("selectedAllFeature", table, {
      table_setSelectedAll: {
        fn: (value: boolean) => {
          t.toggleAllRowsSelected(value);
          t.options.onSelectedAllChange?.(value);
        },
      },
      table_toggleSelectAll: {
        fn: () => t.setSelectedAll(!t.getSelectedAll()),
      },
      table_getSelectedAll: {
        fn: () => t.atoms.selectedAll.get(),
      },
      table_getSelectedCount: {
        fn: () => {
          if (t.atoms.selectedAll.get()) {
            return t.getRowCount();
          }

          const selection = t.atoms.rowSelection.get() ?? {};

          return Object.values(selection).filter(Boolean).length;
        },
      },
      table_getSelectedIds: {
        fn: () => {
          const selection = t.atoms.rowSelection.get() ?? {};

          return Object.entries(selection)
            .filter(([, selected]) => selected)
            .map(([id]) => id);
        },
      },
    });
  },
};
